import { useCallback, useEffect, useState } from "react"
import { fetchLatestInfluencerMedia } from "../../services/api/influencer";
import { MediaType } from "../../types/influencer/influencerTypes";
import Skeleton from "react-loading-skeleton";
import InfiniteScroll from "react-infinite-scroll-component";
import InfluencerPostCard from "../influencer/InfluencerPostCard";
import withModal from "../../utils/hoc/withModal";
import { uploadCollaborationMedia } from "../../services/api/collaboration";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../utils/helpers";
import Badge from "../badges/Badge";
import { useMediaQuery } from "react-responsive";
import { isDesktopMediaQuery } from "../../constants/constants";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux-stores/store";
import { useDispatch } from "react-redux";
import { fetchInfluencerContent, markExpired, setLoading } from "../../redux-stores/slices/influencerContentSlice";
import moment from "moment";
import { fetchCollaborations } from "../../redux-stores/slices/collaborationSlice";
import { fetchCollaboration } from "../../redux-stores/slices/collaborationDetailsSlice";
import { POSTAPIError } from "../../types";

const UploadCollaborationMedia: React.FC<{
    campaignId: string,
    onClose: () => void,
    onUploadError: (error: POSTAPIError) => void
}> = ({ campaignId, onClose, onUploadError }) => {

    const dispatch: AppDispatch = useDispatch();

    const [mediaType, setMediaType] = useState<MediaType>(MediaType.REELS);

    const collaboration = useSelector((state: RootState) => state.collaborations.items.find((cb) => cb.campaign.uuid === campaignId))

    const [selectedPosts, setSelectedPosts] = useState<string[]>([]);

    const [approvedPosts, setApprovedPosts] = useState<string[]>([]);
    const [rejectedPosts, setRejectedPosts] = useState<string[]>([]);

    const data = useSelector(
        (state: RootState) => state.influencerContent.data[mediaType] ?? null
    );

    const posts = data ? data.items.filter((post) => (
        moment((post.posted_on ?? ''), "MMMM D, YYYY, h:mm a").isAfter(moment((collaboration?.collaboration?.created_at ?? ''))) || 
        approvedPosts.includes(post.id) || 
        rejectedPosts.includes(post.id) || 
        selectedPosts.includes(post.id)
    )) : [];

    const has_more = data ? data.has_more : true;
    const page = data ? data.page : 1;

    const loading = useSelector((state: RootState) => state.influencerContent.loading);

    const [uploading, setUploading] = useState<boolean>(false);

    const isDesktop = useMediaQuery(isDesktopMediaQuery)


    const fetchCollaborationMedia = useCallback(async (pageNumber: number, force=false) => {
        if(!campaignId) return;
        dispatch(fetchInfluencerContent({ mediaProductType: mediaType, page: pageNumber, force }));
    }, [campaignId, dispatch, mediaType])


    useEffect(() => {
        if (!campaignId) return;
        fetchCollaborationMedia(1);
    }, [mediaType, campaignId, fetchCollaborationMedia]);

    const nextPage = () => {
        if (has_more && !loading) {
            const nextPageNumber = page + 1;
            fetchCollaborationMedia(nextPageNumber)
        }
    }

    useEffect(() => {
        if(collaboration){
            let selected_posts: string[] = [], rejected_posts: string[] = [], approved_posts: string[] = [];
            if(collaboration?.collaboration && Array.isArray(collaboration.collaboration.posts)){
                for (let post of collaboration.collaboration.posts){
                    if(post.status === 'approved') approved_posts.push(post.ig_post.id);
                    if(post.status === 'rejected') rejected_posts.push(post.ig_post.id);
                    if(post.status === 'pending') selected_posts.push(post.ig_post.id);
                }
            }
            setSelectedPosts(selected_posts);
            setApprovedPosts(approved_posts);
            setRejectedPosts(rejected_posts);
        }
    }, [collaboration])


    const fetchLatestMedia = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {

        try {
            dispatch(setLoading(true));
            await fetchLatestInfluencerMedia();
        } catch (error: any) {
            console.log(error);
            toast.error(getErrorMessage(error));
        } finally {
            dispatch(markExpired());
            await fetchCollaborationMedia(1, true);
        }
    }

    const selectPost = (postId: string) => {
        if(approvedPosts.includes(postId) || rejectedPosts.includes(postId)) return;

        if(selectedPosts.includes(postId)){
            setSelectedPosts([...selectedPosts.filter((pid) => pid !== postId)]);
        }else{
            setSelectedPosts([...selectedPosts, postId])
        }        
    }

    const uploadMedia = async () => {
        try {
            setUploading(true);
            await uploadCollaborationMedia(campaignId, selectedPosts);
            await dispatch(fetchCollaborations({page: 1, force: true}));
            await dispatch(fetchCollaboration({campaignId, force: true}))
            onClose();
            toast.success('Media uploaded successfully!');
        } catch (error: any) {
            console.error(error);
            if(error?.response?.data) {
                onUploadError(error.response.data)
            }else{
                toast.error(getErrorMessage(error));
            }
        } finally {
            setUploading(false);
        }
    }

    return (
        <div className="card card-style m-0 rounded-card pb-3">
            <div className="card-body">
                <div className="card-header p-0 border-0 bg-transparent d-flex justify-content-between align-items-center">
                    <ul className="nav nav-tabs w-100">
                        <li className="nav-item">
                            <a href="#content-reels" onClick={(e) => setMediaType(MediaType.REELS)} className={`nav-link ${mediaType === MediaType.REELS && 'active'}`}>Reels</a>
                        </li>
                        <li className="nav-item">
                            <a href="#content-posts" onClick={(e) => setMediaType(MediaType.POSTS)} className={`nav-link ${mediaType === MediaType.POSTS && 'active'}`}>Posts</a>
                        </li>
                        <li className="nav-item">
                            <a href="#content-stories" onClick={(e) => setMediaType(MediaType.STORIES)} className={`nav-link ${mediaType === MediaType.STORIES && 'active'}`}>Stories</a>
                        </li>
                    </ul>
                    <div className="card-toolbar" style={{ position: 'absolute', right: '1rem', marginBottom: '0.5rem' }}>
                        <button type="button" className="btn btn-xxs ms-2 btn-primary rounded-sm" title="Fetch Latest Media" onClick={fetchLatestMedia}>
                            <i className="fas fa-sync-alt"></i>
                        </button>

                    </div>
                </div>
                <div className="card-body p-0 mt-2" style={{ height: '56vh', overflowY: 'auto' }} id="scrollableContent">
                    <InfiniteScroll
                        className="row m-0"
                        hasMore={has_more}
                        next={nextPage}
                        dataLength={posts.length}
                        loader={<></>}
                        scrollableTarget='scrollableContent'
                    >
                        {posts.map((post, postIndex) => (
                            <div className="col-6 p-1" key={postIndex} style={{ position: 'relative' }} >
                                <InfluencerPostCard post={post} onPostClick={selectPost} />
                                {
                                    !approvedPosts.includes(post.id) && !rejectedPosts.includes(post.id) &&
                                    <div style={{ position: 'absolute', bottom: '60px', marginLeft: 'auto', marginRight: 'auto', left: 0, right: 0, textAlign: 'center' }}>
                                        <input type="checkbox" className="form-check-input text-white bg-transparent post-select-input" style={{
                                            width: '2.25rem',
                                            height: '2.25rem',
                                            border: "2px solid white"
                                        }}
                                            disabled={approvedPosts.includes(post.id) || rejectedPosts.includes(post.id)}
                                            checked={selectedPosts.includes(post.id)}
                                            onChange={() => selectPost(post.id)}
                                        />
                                    </div>
                                }
                                {
                                    (approvedPosts.includes(post.id) || rejectedPosts.includes(post.id)) &&
                                    <div style={{ position: "absolute", top: "20px", marginLeft: 'auto', marginRight: 'auto', left: 0, right: 0, textAlign: 'center' }}>
                                        {
                                            approvedPosts.includes(post.id) &&
                                            <Badge variant='success'><i className="fa fa-check me-1"></i>Approved</Badge>
                                        }
                                        {
                                            rejectedPosts.includes(post.id) &&
                                            <Badge variant="danger"><i className="fa fa-times me-1"></i> Declined</Badge>
                                        }
                                    </div>
                                }
                            </div>
                        ))}
                    </InfiniteScroll>
                    {
                        loading &&
                        <div className="row m-0">
                            {
                                Array.from({ length: 4 }).map((_, i) => {
                                    return (
                                        <div className="col-6 p-1" key={i}>
                                            <Skeleton style={{ width: '100%', height: isDesktop ? '270px' : '145px', padding: 0, margin: 0, borderRadius: '10px' }} />
                                        </div>
                                    )
                                })
                            }
                        </div>
                    }
                    {
                        !loading && !posts.length &&
                        <span className="w-100 d-flex justify-content-center align-items-center" style={{ height: 'calc(100% - 60px)' }}>No media found since collaboration started!</span>
                    }
                </div>
            </div>
            <div className="card-footer">
                <div className="d-flex justify-content-between align-items-center py-2">
                    <span>{selectedPosts.length} media selected.</span>
                    <div>
                        <button type="button" className="btn btn-xxs rounded-sm btn-primary-outlined" onClick={onClose}>Cancel</button>
                        <button type="button" className="btn btn-xxs rounded-sm btn-primary ms-2" disabled={uploading || !selectedPosts.length} onClick={uploadMedia}>{uploading ? 'Uploading...' : 'Upload'}</button>
                    </div>
                </div>
            </div>
        </div>
    );
};


export default withModal(UploadCollaborationMedia)